import { useRefreshAlgoliaData } from "entities/algolia/hooks/useRefreshAlgoliaData";
import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useCreateTagTypeQuery } from "entities/tagTypes/queries/useCreateTagTypeQuery";
import { useServicePrimaryTagListQuery } from "entities/tagTypes/queries/useServicePrimaryTagListQuery";
import { CreateServicePrimaryTag } from "entities/tagTypes/types";
import { hasTagByName } from "entities/tags/api/hasTagByName";
import { useCreateTagQuery } from "entities/tags/queries/useCreateTagQuery";
import {
  invalidateTagDetailsQuery,
  useTagDetailsQuery,
} from "entities/tags/queries/useTagDetailsQuery";
import { invalidateTagsListQuery } from "entities/tags/queries/useTagsListQuery";
import { useUpdateTagQuery } from "entities/tags/queries/useUpdateTagQuery";
import { TagToCreate as CreateTag, TagToUpdate } from "entities/tags/types";
import React, { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { Translations } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { useTagsPage } from "../../../context";
import {
  FormattedTagType,
  TagDetailsProviderProps,
  TagDetailsTabs,
  TagDetailsType,
} from "./types";

const INITIAL_DELAY_MS = 2000;
const REFRESH_INTERVAL_MS = 5000;

const [Provider, useTagDetails] = createContext<TagDetailsType>({
  name: "TagDetailsContext",
  hookName: "useTagDetails",
  providerName: "TagDetailsProvider",
});

const TagDetailsProvider: FC<TagDetailsProviderProps> = ({ children }) => {
  const { allowed: hasReadAccess } = useRolePermission("tags:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "tags:translations:read",
  );
  const { tagsDetailsDrawer } = useTagsPage();
  const { drawerDetails } = tagsDetailsDrawer;
  const [selectedTab, setSelectedTab] = useState(TagDetailsTabs.General);
  const [tagId, setTagId] = useState<string>();
  const tagName: InputHookResult = useInputState("tagName", "");
  const tagDescription: InputHookResult = useInputState("tagDescription", "");
  const [tagType, setTagType] = useState<FormattedTagType | null>(null);
  const tagTypeInput: InputHookResult = useInputState("tagType", "");
  const [errors, setErrors] = useState<string>("");
  const { tableData: tags } = useTagsPage();
  const [translations, setTranslations] = useState<Translations | undefined>();
  const { refreshAlgoliaData } = useRefreshAlgoliaData();

  const { data: servicePrimaryTags, isLoading: isLoadingTagTypes } =
    useServicePrimaryTagListQuery();
  const { data: tagData, isLoading } = useTagDetailsQuery(
    drawerDetails?.id || "",
  );

  const { mutate: updateTagDetails, isPending: isSubmittingTag } =
    useUpdateTagQuery();

  const { mutate: createTagDetails, isPending } = useCreateTagQuery();

  const { mutate: createTagType, isPending: isSubmittingTagType } =
    useCreateTagTypeQuery();

  const setDefaultTag = () => {
    tagName.setValue("");
    tagDescription.setValue("");
    tagName?.setErrors([]);
    setTagType(null);
    tagTypeInput.setValue("");
    setTranslations(undefined);
  };

  const disabled = useMemo(
    () =>
      !tagName?.input.value ||
      Boolean(tagName.input.errors.length || !tagType?.id),
    [tagName.input.errors.length, tagName.input.value, tagType?.id],
  );

  useEffect(() => {
    if (tagData) {
      setTagId(tagData?.id);
      tagName.setValue(tagData.name);
      tagDescription.setValue(tagData.description);
      setTranslations(tagData.translations);

      const tagTypeData = servicePrimaryTags?.find(
        (tagType) => tagData.tagType === tagType.id,
      );

      setTagType({
        id: tagTypeData?.id || "",
        name: tagTypeData?.name || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagData, isLoadingTagTypes]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(TagDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, tagName]);

  const performTagNameError = (valueIsExist: boolean) => {
    if (valueIsExist) {
      return tagName?.setErrors([
        { key: "tagName", message: "This tag name is already exists" },
      ]);
    }

    return tagName?.setErrors([]);
  };

  const onChangeTagName = (e: ChangeEvent<HTMLInputElement>) => {
    tagName?.input.onChange(e);
    tagName?.setErrors([]);
  };

  const onCreateTagType = async (tagType: CreateServicePrimaryTag) => {
    return createTagType({ tagType, handleTagType: setTagType });
  };

  const onUpdateTagDetails = async (tagId: string, tag: TagToUpdate) => {
    return updateTagDetails({ tagId, tag });
  };

  const onCreateTag = async (tag: CreateTag) => {
    return createTagDetails({ tag });
  };

  const onSubmitTag = async () => {
    if (disabled) {
      return;
    }

    try {
      const tagToSubmit = {
        name: tagName?.input.value.trim(),
        description: tagDescription?.input.value,
        matchingGamesCount: tagData ? tagData.matchingGamesCount : 0,
        tagType: tagType?.id || "",
        tagTypeName: tagType?.name || "",
        translations: translations || {},
        updatedAt: new Date(),
      };

      const isTagExist = await hasTagByName(tagToSubmit.name, tagId);

      const isDuplicateTag = tags.data.some(
        (tag) =>
          tag.tagType === tagToSubmit.tagType && tag.name === tagToSubmit.name,
      );

      if (isTagExist && isDuplicateTag) {
        return performTagNameError(isTagExist);
      }

      if (tagId) {
        await onUpdateTagDetails(tagId, { ...tagToSubmit });
        invalidateTagDetailsQuery(tagId);
      } else {
        await onCreateTag({ ...tagToSubmit });
      }

      // Invalidate query and refetch Algolia
      refreshAlgoliaData(INITIAL_DELAY_MS);
      refreshAlgoliaData(REFRESH_INTERVAL_MS);

      tagsDetailsDrawer.closeDrawer();
      setTagId(undefined);
      setDefaultTag();
      invalidateTagsListQuery();
    } catch (e: any) {
      console.error(e);
    }
  };

  const onSubmitTagTranslation = async () => {
    if (disabled) {
      return;
    }

    if (!tagId) {
      return;
    }

    try {
      const tagToSubmit = {
        translations: translations || {},
        updatedAt: new Date(),
      };

      await onUpdateTagDetails(tagId, { ...tagToSubmit });
      invalidateTagDetailsQuery(tagId);

      // Invalidate query and refetch Algolia
      refreshAlgoliaData(INITIAL_DELAY_MS);
      refreshAlgoliaData(REFRESH_INTERVAL_MS);

      tagsDetailsDrawer.closeDrawer();
      setTagId(undefined);
      setDefaultTag();
      invalidateTagsListQuery();
    } catch (e: any) {
      console.error(e);
    }
  };

  const onSubmitTagType = async () => {
    if (!tagTypeInput.input.value.length) {
      return;
    }

    try {
      const tagTypeToSubmit = {
        name: tagTypeInput.input.value.trim(),
        createdAt: new Date(),
      };

      await onCreateTagType(tagTypeToSubmit);
    } catch (e: any) {
      console.error(e);
    }
  };

  const onCloseTagDetails = () => {
    setDefaultTag();
    tagsDetailsDrawer.closeDrawer();
    setTagId(undefined);
    setSelectedTab(TagDetailsTabs.General);
  };

  const value = {
    loading: isLoading,
    submitting: isSubmittingTag || isPending || isSubmittingTagType,
    isTagTypesLoading: isSubmittingTagType || isLoadingTagTypes,
    selectedTab,
    setSelectedTab,
    translations,
    setTranslations,
    errors,
    tagId,
    tagName,
    tagDescription,
    disabled,
    onSubmitTag,
    onSubmitTagTranslation,
    onSubmitTagType,
    tagType,
    tagTypeInput,
    servicePrimaryTags,
    setTagType,
    setTagId,
    setDefaultTag,
    setErrors,
    onChangeTagName,
    onCloseTagDetails,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { TagDetailsProvider, useTagDetails };
